import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { api } from "@constructionautomation/backend";
import { Loader2 } from "lucide-react";

import { AppLink } from "@/components/app-link";
import { PageHeader } from "@/components/layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AccessStatusBadge,
  SeatSyncStatusBadge,
} from "@/features/admin-billing/billing-status-badges";
import { toTitleCase } from "@/lib/formatting";

type BillingRow = {
  organizationId: string;
  organizationName: string;
  billingMode: string | null;
  accessStatus: string;
  billedSeats: number | null;
  activeMembers: number;
  billableSeats: number;
  seatSyncStatus: string;
  requiresReconciliation: boolean;
  renewalDate: number | null;
  isProviderBacked: boolean;
};

export const Route = createFileRoute("/admin/billing/")({
  component: AdminBillingIndexPage,
});

function formatDate(value: number | null) {
  if (!value) {
    return "-";
  }
  return new Date(value).toLocaleDateString();
}

function formatMode(value: string | null) {
  return value ? toTitleCase(value) : "-";
}

function AdminBillingIndexPage() {
  const { data, isPending, error } = useQuery(
    convexQuery(api.admin.billing.listOrganizationBilling, {})
  );

  if (isPending) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="size-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-24 text-muted-foreground">
        Failed to load organization billing.
      </div>
    );
  }

  const rows = data as BillingRow[];
  const needsAttention = rows.filter((row) => row.requiresReconciliation).length;
  const manualEntitlements = rows.filter(
    (row) => row.billingMode !== null && !row.isProviderBacked
  ).length;

  return (
    <div className="space-y-4">
      <PageHeader title="Billing" />

      <div className="grid gap-4 md:grid-cols-3">
        <SummaryCard label="Organizations" value={String(rows.length)} />
        <SummaryCard label="Needs Reconciliation" value={String(needsAttention)} />
        <SummaryCard label="Manual Entitlements" value={String(manualEntitlements)} />
      </div>

      <Card className="py-4">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Organization Billing</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="font-bold uppercase tracking-wider text-xs">
                    Organization
                  </TableHead>
                  <TableHead className="font-bold uppercase tracking-wider text-xs">
                    Mode
                  </TableHead>
                  <TableHead className="font-bold uppercase tracking-wider text-xs">
                    Access
                  </TableHead>
                  <TableHead className="font-bold uppercase tracking-wider text-xs">
                    Seat Status
                  </TableHead>
                  <TableHead className="font-bold uppercase tracking-wider text-xs text-right">
                    Billed
                  </TableHead>
                  <TableHead className="font-bold uppercase tracking-wider text-xs text-right">
                    Paid seats
                  </TableHead>
                  <TableHead className="font-bold uppercase tracking-wider text-xs">
                    Renewal
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="py-8 text-center text-muted-foreground">
                      No organizations yet.
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((row) => (
                    <TableRow key={row.organizationId}>
                      <TableCell>
                        <AppLink
                          to={`/admin/billing/${row.organizationId}`}
                          className="font-medium text-foreground hover:underline"
                        >
                          {row.organizationName}
                        </AppLink>
                      </TableCell>
                      <TableCell>{formatMode(row.billingMode)}</TableCell>
                      <TableCell>
                        <AccessStatusBadge status={row.accessStatus} />
                      </TableCell>
                      <TableCell>
                        <SeatSyncStatusBadge status={row.seatSyncStatus} />
                      </TableCell>
                      <TableCell className="text-right">{row.billedSeats ?? "-"}</TableCell>
                      <TableCell className="text-right">{row.billableSeats}</TableCell>
                      <TableCell>{formatDate(row.renewalDate)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function SummaryCard(props: { label: string; value: string }) {
  return (
    <div className="rounded-lg border-2 border-border bg-card px-6 py-4">
      <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
        {props.label}
      </p>
      <p className="mt-1 text-3xl font-bold tracking-tight text-foreground">{props.value}</p>
    </div>
  );
}
